import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { getClientId, getStoredName, setStoredName, genRoomCode } from "@/lib/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Brain, Users, Sparkles, UserCircle2 } from "lucide-react";
import { SAMEBRAIN_GAME_ENABLED } from "@/lib/featureFlags";
import { ThemeToggle } from "@/components/ThemeToggle";

const Index = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [name, setName] = useState(getStoredName());
  const [joinCode, setJoinCode] = useState("");
  const [mode, setMode] = useState<"game" | "poker" | "retro">(
    SAMEBRAIN_GAME_ENABLED ? "game" : "poker"
  );
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Enter your name first");
      return;
    }
    setCreating(true);
    setStoredName(name.trim());

    // Resolve workspace + tier for signed-in hosts
    let workspaceId: string | null = null;
    let maxPlayers = 30; // free anonymous default
    if (user) {
      const activeId = localStorage.getItem("samebrain.activeWorkspaceId");
      const { data: members } = await supabase
        .from("workspace_members")
        .select("workspace_id")
        .eq("user_id", user.id);
      const ids = (members ?? []).map((m: any) => m.workspace_id);
      workspaceId = activeId && ids.includes(activeId) ? activeId : ids[0] ?? null;
      if (workspaceId) {
        const { data: sub } = await supabase
          .from("subscriptions")
          .select("tier")
          .eq("workspace_id", workspaceId)
          .maybeSingle();
        const { maxPlayersFor } = await import("@/lib/limits");
        maxPlayers = maxPlayersFor(sub?.tier as any);
      }
    }

    const code = genRoomCode();
    const clientId = getClientId();
    const { data: room, error } = await supabase
      .from("rooms")
      .insert({
        code,
        mode,
        host_id: clientId,
        status: "lobby",
        workspace_id: workspaceId,
        max_players: maxPlayers,
        settings: mode === "game" ? { rounds: 5, timer: 30, category: "random" } : {},
      })
      .select()
      .single();
    if (error || !room) {
      toast.error("Couldn't create room");
      setCreating(false);
      return;
    }
    await supabase.from("players").insert({
      room_id: room.id,
      client_id: clientId,
      name: name.trim(),
      is_host: true,
      team: mode === "game" ? "purple" : null,
    });
    navigate(`/room/${code}`);
  };

  const handleJoin = async () => {
    if (!name.trim()) {
      toast.error("Enter your name first");
      return;
    }
    if (joinCode.trim().length !== 4) {
      toast.error("Room codes are 4 characters");
      return;
    }
    setStoredName(name.trim());
    navigate(`/room/${joinCode.trim().toUpperCase()}`);
  };

  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-4 py-10 relative">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <ThemeToggle />
        <Link to="/pricing">
          <Button variant="ghost" size="sm">Pricing</Button>
        </Link>
        <Link to={user ? "/account" : "/auth"}>
          <Button variant="ghost" size="sm">
            <UserCircle2 className="h-4 w-4 mr-1.5" />
            {user ? "Account" : "Sign in"}
          </Button>
        </Link>
      </div>
      <div className="w-full max-w-md space-y-8 animate-pop-in">
        <header className="text-center space-y-3">
          <div className="inline-flex h-20 w-20 items-center justify-center rounded-3xl gradient-fun shadow-pop">
            <Brain className="h-10 w-10 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="text-5xl font-extrabold tracking-tight">
            Same<span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Brain</span>
          </h1>
          <p className="text-muted-foreground text-lg">Discover how aligned your team really is.</p>
        </header>

        <Card className="p-6 space-y-5 shadow-card border-2">
          <div className="space-y-2">
            <label className="text-sm font-semibold">Your name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Alex"
              maxLength={20}
              className="h-12 text-base"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold">Pick a mode</label>
            <div className={`grid ${SAMEBRAIN_GAME_ENABLED ? "grid-cols-3" : "grid-cols-2"} gap-2`}>
              {SAMEBRAIN_GAME_ENABLED && (
                <button
                  onClick={() => setMode("game")}
                  className={`rounded-xl border-2 p-3 text-left transition-bounce ${
                    mode === "game"
                      ? "border-primary bg-primary/10 shadow-soft"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <Sparkles className="h-5 w-5 text-primary mb-1" />
                  <div className="font-bold text-xs">SameBrain</div>
                  <div className="text-[10px] text-muted-foreground">Match teammates</div>
                </button>
              )}
              <button
                onClick={() => setMode("poker")}
                className={`rounded-xl border-2 p-3 text-left transition-bounce ${
                  mode === "poker"
                    ? "border-primary bg-primary/10 shadow-soft"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <Users className="h-5 w-5 text-accent mb-1" />
                <div className="font-bold text-xs">Scrum Poker</div>
                <div className="text-[10px] text-muted-foreground">Estimate stories</div>
              </button>
              <button
                onClick={() => setMode("retro")}
                className={`rounded-xl border-2 p-3 text-left transition-bounce ${
                  mode === "retro"
                    ? "border-primary bg-primary/10 shadow-soft"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <span className="block text-xl mb-1 leading-none">🪞</span>
                <div className="font-bold text-xs">Retro</div>
                <div className="text-[10px] text-muted-foreground">Sprint review</div>
              </button>
            </div>
          </div>

          <Button
            onClick={handleCreate}
            disabled={creating}
            size="lg"
            className="w-full h-14 text-base font-bold gradient-primary shadow-pop hover:opacity-95"
          >
            Create Room
          </Button>
        </Card>

        <Card className="p-6 space-y-3 shadow-card border-2">
          <label className="text-sm font-semibold">Or join with a code</label>
          <div className="flex gap-2">
            <Input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="ABCD"
              maxLength={4}
              className="h-12 text-2xl font-bold tracking-[0.3em] text-center uppercase"
            />
            <Button onClick={handleJoin} size="lg" variant="secondary" className="h-12 px-6 font-bold">
              Join
            </Button>
          </div>
        </Card>

        <p className="text-center text-xs text-muted-foreground">No account needed · Plays in your browser</p>
      </div>
    </main>
  );
};

export default Index;
